/**
 * <Overlay> — the reusable portal-backed scrim shell (atom, L0.1).
 *
 * Encapsulates react-native-paper's `<Portal>` (a JS-layer overlay — no native iOS `UIViewController`,
 * so an in-overlay image picker / PHPicker still presents) plus everything every dialog/sheet needs and
 * must not re-hand-roll: a full-screen scrim, a dismiss-on-backdrop `<Pressable>`, Android hardware-back
 * and web-Escape handling, and the `DimensionsContext` re-provide (paper's Portal doesn't forward React
 * context). It owns ONLY the shell — each consumer (`<Modal>`, `<ActionSheet>`, `<ConfirmDialog>`) styles
 * its own panel and passes it as `children`.
 *
 * `placement` sets vertical anchoring: `center` (dialogs) or `bottom` (sheets). The panel is always
 * horizontally centered — full-width bottom sheets simply set `width: "100%"` on their own panel.
 * Backdrop press + back + Escape are all gated by `dismissable`.
 * Optional `withCloseButton` paints a top-right times `<IconButton>` that calls `onRequestClose`
 * (used by media viewers; Modal keeps its own header close). It sits on its own night disc
 * (`closeButtonFace`): it lands on the scrim or on a viewer's light panel, and must read on both.
 *
 * `placement="anchor"` pose une liste sous son champ (`anchor`, mesuré dans la fenêtre), sans voile : la liste d'un
 * champ d'autocomplétion (`<SelectField presentation="inline">`). Elle descend sous le champ, à sa largeur, et monte
 * au-dessus quand la place manque en bas (`anchoredFrame`). On la ferme en touchant ailleurs : au web, un clic hors
 * d'elle et du champ, qui part ensuite à ce qu'il visait (la page reste vivante), ou un défilement de la page ; au
 * toucher, un fond transparent. Échap et le retour d'Android, comme les autres.
 *
 * L'entrée et la sortie de toutes les feuilles et de toutes les boîtes sont ici (`overlayPresence`) :
 * le voile entre en fondu ; une feuille (`bottom`) monte du bas, une boîte centrée entre en fondu ; à la
 * fermeture, l'inverse, et l'overlay ne se démonte qu'une fois la sortie finie. Pendant sa sortie, il
 * garde ce qu'il montrait (ses derniers enfants) et ne répond plus au toucher. En mouvement réduit,
 * rien ne bouge. Aucune feuille ne s'anime elle-même.
 */
import React, { useContext, useEffect, useRef, useState } from "react"
import { Animated, Dimensions, Keyboard, Platform, Pressable, View } from "react-native"
import { Portal } from "react-native-paper"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { DimensionsContext } from "../../../common/utils/mobileUtils"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { holdEscape, listenEscape } from "../../utils/listenEscape"
import { IconButton } from "../Buttons/IconButton"
import { anchoredFrame, type AnchoredFrame, type AnchorRect } from "./anchoredFrame"
import { CLOSE_BUTTON_FILL, CLOSE_BUTTON_INK } from "./closeButtonFace"
import { overlayPanelMotion, useOverlayPresence } from "./overlayPresence"

type Placement = "center" | "bottom" | "anchor"

export type { AnchorRect as OverlayAnchor }

/**
 * Le liseré du bord haut d'une feuille qui monte du bas (`placement="bottom"`), 1 px dans le jeton
 * `sheet-edge`. Chaque feuille le pose sur son panneau : l'overlay ne dessine pas le panneau.
 */
export const SHEET_EDGE_WIDTH = 1

export type OverlayProps = {
    visible: boolean
    /** Backdrop tap, Android back and web Escape all call this (when `dismissable`). */
    onRequestClose: () => void
    /** Enables backdrop-press + back + Escape dismissal. Defaults to true. */
    dismissable?: boolean
    /**
     * Hide the portal visually without unmounting (keeps form/media state). Used when a camera /
     * Genius Scan flow must present above the JS overlay — same as legacy `display: none` on cameraOn.
     */
    hidden?: boolean
    /**
     * Panel anchoring: `center` (dialog), `bottom` (sheet), or `anchor` — une liste sous le champ `anchor`, sans voile.
     * Defaults to `center`.
     */
    placement?: Placement
    /** Le champ sous lequel se pose la liste (`placement="anchor"`), mesuré dans la fenêtre (`measureInWindow`). */
    anchor?: AnchorRect | null
    /**
     * Show a top-right times close button that calls `onRequestClose`. Defaults to false —
     * Modal / ActionSheet / ConfirmDialog keep their own dismiss chrome.
     */
    withCloseButton?: boolean
    /**
     * `deep` : le voile redoublé, pour une surface dont ce qui compte est hors de la page (le guidage de
     * la question du navigateur). Il entre et sort en fondu avec le voile, quel que soit `placement`.
     * Défaut `standard`.
     */
    scrim?: "standard" | "deep"
    children: React.ReactNode
    testID?: string
}

export function Overlay(props: OverlayProps) {
    const {
        visible,
        onRequestClose,
        dismissable = true,
        hidden = false,
        placement = "center",
        withCloseButton = false,
        scrim = "standard",
        anchor = null,
        children,
        testID,
    } = props
    const anchored = placement === "anchor"

    // Read the context here (outside the Portal, where the app provider is in scope); fall back to raw
    // window dims for Storybook / no-provider. Re-provided inside the Portal below.
    const dimensions = useContext(DimensionsContext) ?? Dimensions.get("window")
    const insets = useSafeAreaInsets()
    const reduceMotion = useReduceMotion()
    const { motion } = useUnistyles().theme
    const { mounted, closing, progress } = useOverlayPresence(visible, reduceMotion, motion)
    // Pendant la sortie, le panneau garde ce qu'il montrait : l'appelant a pu déjà vider ses données.
    const kept = useRef(children)
    if (visible) kept.current = children

    // L'inscription ne se refait pas à chaque rendu de l'appelant : elle remettrait le calque en haut de la pile
    // d'Échap, au-dessus de la liste ouverte par-dessus lui.
    const close = useRef(onRequestClose)
    close.current = onRequestClose
    useEffect(() => {
        // While hidden (camera trip), let the camera own Android back / Escape.
        if (!visible || hidden) return
        // Une boîte qu'on ne ferme pas d'un geste garde Échap : la vue du dessous ne se ferme pas sous elle.
        return dismissable ? listenEscape(() => close.current()) : holdEscape()
    }, [visible, dismissable, hidden])

    const frame = useAnchoredFrame(anchored && visible, anchor)
    useOutsidePress(anchored && visible && dismissable && Platform.OS === "web", frame.panelRef, anchor, onRequestClose)

    if (!mounted) return null

    if (anchored) {
        return (
            <Portal>
                <DimensionsContext.Provider value={dimensions}>
                    <View
                        ref={frame.hostRef}
                        collapsable={false}
                        onLayout={frame.measureHost}
                        style={[styles.overlay, hidden && styles.hidden]}
                        // Au web, la page reste vivante sous la liste : un clic ailleurs la ferme puis part à ce qu'il visait.
                        pointerEvents={closing ? "none" : "box-none"}
                        testID={testID}
                    >
                        {dismissable && !hidden && !closing && Platform.OS !== "web" && (
                            <Pressable style={styles.layer} onPress={onRequestClose} accessibilityLabel="Fermer" />
                        )}
                        {frame.value != null && (
                            <Animated.View style={[styles.anchored(frame.value), { opacity: progress }]}>
                                <View ref={frame.panelRef} collapsable={false} style={styles.anchoredBody}>
                                    {kept.current}
                                </View>
                            </Animated.View>
                        )}
                    </View>
                </DimensionsContext.Provider>
            </Portal>
        )
    }

    return (
        <Portal>
            <DimensionsContext.Provider value={dimensions}>
                <View
                    style={[styles.overlay, hidden && styles.hidden]}
                    pointerEvents={closing ? "none" : "auto"}
                    testID={testID}
                >
                    <Animated.View style={[styles.layer, styles.scrim, { opacity: progress }]} />
                    {scrim === "deep" && <Animated.View style={[styles.layer, styles.scrim, { opacity: progress }]} />}
                    {dismissable && !hidden && !closing && (
                        <Pressable style={styles.layer} onPress={onRequestClose} accessibilityLabel="Fermer" />
                    )}
                    <Animated.View
                        style={[
                            styles.layer,
                            styles.panel(placement),
                            overlayPanelMotion(
                                placement === "bottom" ? "bottom" : "center",
                                progress,
                                dimensions.height
                            ),
                        ]}
                        pointerEvents="box-none"
                    >
                        {kept.current}
                    </Animated.View>
                    {withCloseButton && !hidden && (
                        <Animated.View
                            style={[styles.closeButton(insets.top), { opacity: progress }]}
                            pointerEvents="box-none"
                        >
                            <IconButton
                                iconName="times"
                                size={44}
                                iconSize={20}
                                color={CLOSE_BUTTON_FILL}
                                iconColor={CLOSE_BUTTON_INK}
                                onPress={onRequestClose}
                                accessibilityLabel="Fermer"
                            />
                        </Animated.View>
                    )}
                </View>
            </DimensionsContext.Provider>
        </Portal>
    )
}

/**
 * Le cadre d'une liste ancrée : le calque se mesure dans la fenêtre (son origine n'est pas celle de la fenêtre sur
 * Android, entre les barres), le champ arrive mesuré dans la fenêtre, et le clavier ouvert raccourcit le bas.
 */
function useAnchoredFrame(active: boolean, anchor: AnchorRect | null) {
    const hostRef = useRef<View>(null)
    const panelRef = useRef<View>(null)
    const [host, setHost] = useState<AnchorRect | null>(null)
    // Le clavier ne se lit que sur un téléphone : `Keyboard.metrics` n'existe pas au web (react-native-web), et l'appeler
    // là faisait tomber toute surface qui pose un overlay.
    const [keyboardTop, setKeyboardTop] = useState<number | null>(() =>
        Platform.OS !== "web" && typeof Keyboard.metrics === "function" ? (Keyboard.metrics()?.screenY ?? null) : null
    )

    useEffect(() => {
        if (!active || Platform.OS === "web") return
        const shown = Keyboard.addListener("keyboardDidShow", (event) => setKeyboardTop(event.endCoordinates.screenY))
        const hidden = Keyboard.addListener("keyboardDidHide", () => setKeyboardTop(null))
        return () => {
            shown.remove()
            hidden.remove()
        }
    }, [active])

    const measureHost = () =>
        hostRef.current?.measureInWindow((x, y, width, height) => setHost({ x, y, width, height }))

    const value: AnchoredFrame | null =
        host != null && anchor != null
            ? anchoredFrame({
                  anchor: { ...anchor, x: anchor.x - host.x, y: anchor.y - host.y },
                  host,
                  keyboardTop: keyboardTop != null ? keyboardTop - host.y : null,
              })
            : null
    return { hostRef, panelRef, measureHost, value }
}

type WebNode = { contains: (other: unknown) => boolean }
const SCROLL_GRACE_MS = 400
type WebPointerEvent = { target: unknown; clientX: number; clientY: number; preventDefault: () => void }
type WebDocument = {
    addEventListener: (type: string, listener: (event: WebPointerEvent) => void, capture: boolean) => void
    removeEventListener: (type: string, listener: (event: WebPointerEvent) => void, capture: boolean) => void
}

/**
 * Au web, une liste ancrée se ferme sur un clic hors d'elle et de son champ, et quand la page défile (elle ne
 * suivrait plus son champ). Un appui DANS la liste garde le curseur dans le champ : on continue de taper.
 */
function useOutsidePress(
    active: boolean,
    panelRef: React.RefObject<View | null>,
    anchor: AnchorRect | null,
    onClose: () => void
) {
    const latest = useRef({ anchor, onClose })
    latest.current = { anchor, onClose }

    useEffect(() => {
        const doc = (globalThis as { document?: WebDocument }).document
        if (!active || doc == null) return
        const inPanel = (target: unknown) => (panelRef.current as unknown as WebNode | null)?.contains(target) === true
        const onPointerDown = (event: WebPointerEvent) => {
            if (inPanel(event.target)) {
                event.preventDefault()
                return
            }
            const field = latest.current.anchor
            const onField =
                field != null &&
                event.clientX >= field.x &&
                event.clientX <= field.x + field.width &&
                event.clientY >= field.y &&
                event.clientY <= field.y + field.height
            if (!onField) latest.current.onClose()
        }
        // Le navigateur fait défiler la page pour amener le champ qui prend le curseur : ce défilement-là, juste
        // après l'ouverture, ne ferme pas la liste.
        const openedAt = Date.now()
        const onScroll = (event: WebPointerEvent) => {
            if (Date.now() - openedAt < SCROLL_GRACE_MS) return
            if (!inPanel(event.target)) latest.current.onClose()
        }
        doc.addEventListener("mousedown", onPointerDown, true)
        doc.addEventListener("scroll", onScroll, true)
        return () => {
            doc.removeEventListener("mousedown", onPointerDown, true)
            doc.removeEventListener("scroll", onScroll, true)
        }
    }, [active, panelRef])
}

const styles = StyleSheet.create((theme) => ({
    overlay: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        elevation: 1000,
    },
    hidden: {
        display: "none" as const,
    },
    /** Un calque de la taille de l'écran : le voile, le fond qui ferme, le panneau. */
    layer: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    /** Le voile des feuilles et des boîtes : `backdrop` partout, plus sombre chez l'élève. */
    scrim: {
        backgroundColor: theme.colors.scrim,
    },
    /**
     * Le calque du panneau a la taille de l'écran, pour que les largeurs et hauteurs en % des
     * panneaux s'y lisent comme avant ; il laisse passer le toucher hors du panneau, vers le fond.
     * `placement` drives only vertical anchoring; the panel is always centered horizontally.
     */
    panel: (placement: Placement) => ({
        justifyContent: (placement === "bottom" ? "flex-end" : "center") as "flex-end" | "center",
        alignItems: "center" as const,
    }),
    /** La liste ancrée : posée sur son cadre, sa hauteur plafonnée ; son contenu défile au-delà. */
    anchored: (frame: AnchoredFrame) => ({
        position: "absolute" as const,
        left: frame.left,
        width: frame.width,
        maxHeight: frame.maxHeight,
        ...(frame.top != null ? { top: frame.top } : { bottom: frame.bottom }),
    }),
    anchoredBody: { flexShrink: 1, maxHeight: "100%" as const },
    closeButton: (topInset: number) => ({
        position: "absolute" as const,
        top: topInset + theme.spacing["2"],
        right: theme.spacing["3"],
        zIndex: 20,
    }),
}))
